import type { FC, ReactNode } from 'react'
import type { Testimonial, Tone } from '@/types'

import { useEffect, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { cn } from '@/lib/utils'
import { EASE_OUT_EXPO, Reveal } from '@/components/motion/reveal'
import { ToneSection } from '@/components/generals/tone'

const DURATION = 9000

interface TestimonialsProps {
    items: Testimonial[]
    tone?: Tone
    label?: string
    id?: string
}

// An index of voices on one side, the selected quote set large on the other.
// Advances on its own, pauses while hovered or focused.
export const Testimonials: FC<TestimonialsProps> = ({ items, tone = 'paper', label = 'In their words', id = 'testimonials' }): ReactNode => {
    const reduce = useReducedMotion()
    const [active, setActive] = useState(0)
    const [paused, setPaused] = useState(false)
    const current = items[active]

    useEffect(() => {
        if (reduce || paused || items.length < 2) return

        const timer = window.setTimeout(() => setActive(value => (value + 1) % items.length), DURATION)

        return () => {
            window.clearTimeout(timer)
        }
    }, [active, paused, reduce, items.length])

    return (
        <ToneSection tone={tone} id={id} className='px-5 py-28 md:px-8 lg:px-12 lg:py-40'>
            <Reveal className='mb-14 flex items-end justify-between gap-6 lg:mb-20'>
                <h2 className='font-heading t-h2 font-semibold uppercase wdth-75'>
                    {label}
                </h2>
                {items.length > 1 && (
                    <span className='t-label text-tone-muted tabular-nums'>
                        0{active + 1} / 0{items.length}
                    </span>
                )}
            </Reveal>

            <div
                className='grid gap-14 lg:grid-cols-12'
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                onFocus={() => setPaused(true)}
                onBlur={() => setPaused(false)}
            >
                <div role='tablist' aria-label='Client testimonials' className={cn('flex flex-col lg:col-span-4', items.length < 2 && 'hidden')}>
                    {items.map((item, index) => (
                        <button
                            key={item.name}
                            type='button'
                            role='tab'
                            id={`${id}-tab-${index}`}
                            aria-selected={index === active}
                            aria-controls={`${id}-panel`}
                            onClick={() => setActive(index)}
                            className='group relative flex items-center justify-between gap-4 border-t border-tone-line py-4 text-left last:border-b'
                        >
                            <span className='flex flex-col gap-1'>
                                <span className={cn('font-heading text-xl font-semibold tracking-tight transition-opacity duration-500 md:text-2xl', index === active ? 'opacity-100' : 'opacity-40 group-hover:opacity-80')}>
                                    {item.company}
                                </span>
                                <span className='t-label text-tone-muted'>
                                    {item.name}
                                </span>
                            </span>

                            <span className='t-label text-tone-muted tabular-nums'>
                                0{index + 1}
                            </span>

                            <span className='absolute inset-x-0 -top-px h-px overflow-hidden' aria-hidden>
                                {index === active && (
                                    <motion.span
                                        key={`${active}-${paused}`}
                                        className='block h-full origin-left bg-tone'
                                        initial={{ scaleX: paused || reduce ? 1 : 0 }}
                                        animate={{ scaleX: 1 }}
                                        transition={{ duration: paused || reduce ? 0 : DURATION / 1000, ease: 'linear' }}
                                    />
                                )}
                            </span>
                        </button>
                    ))}
                </div>

                <div
                    id={`${id}-panel`}
                    role='tabpanel'
                    aria-labelledby={`${id}-tab-${active}`}
                    className={cn('relative min-h-[26rem]', items.length < 2 ? 'lg:col-span-10 lg:col-start-2' : 'lg:col-span-8')}
                >
                    <AnimatePresence mode='wait'>
                        <motion.figure
                            key={current.name}
                            className='flex h-full flex-col gap-10'
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.35 }}
                        >
                            <div className='flex flex-wrap items-end justify-between gap-6'>
                                <div className='overflow-hidden'>
                                    <motion.p
                                        className='font-heading text-[clamp(4.5rem,11vw,10rem)] font-semibold leading-[0.8] tracking-[-0.05em] wdth-62'
                                        initial={{ y: '100%' }}
                                        animate={{ y: '0%' }}
                                        transition={{ duration: 0.9, ease: EASE_OUT_EXPO }}
                                    >
                                        {current.metric}
                                    </motion.p>
                                </div>
                                <p className='max-w-[14rem] t-label text-tone-muted'>
                                    {current.metricLabel}
                                </p>
                            </div>

                            <blockquote className='flex flex-col gap-5'>
                                {current.quote.map((paragraph, index) => (
                                    <motion.p
                                        key={index}
                                        className={cn(
                                            'font-serif leading-[1.18] tracking-[-0.01em]',
                                            index === 0 ? 'text-[clamp(1.6rem,2.6vw,2.6rem)]' : 'text-[clamp(1.15rem,1.5vw,1.5rem)] text-tone-muted'
                                        )}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.8, ease: EASE_OUT_EXPO, delay: 0.1 + index * 0.08 }}
                                    >
                                        {index === 0 && '“'}
                                        {paragraph}
                                        {index === current.quote.length - 1 && '”'}
                                    </motion.p>
                                ))}
                            </blockquote>

                            <figcaption className='mt-auto flex items-center gap-5 border-t border-tone-line pt-6'>
                                <span className='flex h-12 w-28 items-center justify-center bg-void px-3'>
                                    <img src={current.logo} alt={`${current.company} logo`} width={400} height={187} className='h-full w-full object-contain' />
                                </span>
                                <span className='flex flex-col'>
                                    <span className='font-semibold'>
                                        {current.name}
                                    </span>
                                    <span className='text-sm text-tone-muted'>
                                        {current.role}, {current.company}
                                    </span>
                                </span>
                            </figcaption>
                        </motion.figure>
                    </AnimatePresence>
                </div>
            </div>
        </ToneSection>
    )
}
